/**
 * Page templates — MTC-FCS-016, and the Mockup Maker imports.
 * ============================================================================
 * Four arrangements of the same website. Not four websites: the content model
 * does not change, the sections do not change, and nothing a contractor has
 * typed is tied to one of these. Only the shape of the page is.
 *
 * ── THEY ARE CSS, NOT FOUR COMPONENTS ──────────────────────────────────────
 * Every template renders the SAME markup, and `data-template` on the root is
 * what selects between them. That is a deliberate constraint, and it buys
 * three things worth more than the freedom it gives up:
 *
 *   1. Switching template can never lose content, because there is nothing to
 *      re-map — the DOM is identical.
 *   2. The preview, the downloaded file and the published page cannot disagree,
 *      because there is one renderer.
 *   3. A Freemium payment can be enforced server-side by rewriting ONE
 *      attribute. Four component trees would mean parsing the page to undo a
 *      template, which is not a thing to be doing on the publish path.
 *
 * ── SPLIT AND BOLD WERE REMOVED ────────────────────────────────────────────
 * They were arrangements of this builder's own markup. The five Mockup Maker
 * designs below are real pages with their own markup and stylesheets, so the
 * two kinds sat oddly beside each other in one list — and a contractor
 * choosing between nine things chooses worse than between seven.
 *
 * ── CLASSIC IS THE FREE ONE ────────────────────────────────────────────────
 * Everything else is Premium, and `activeTemplate` in lib/site-draft.js is
 * what enforces that in the preview. The publish route enforces it for real.
 *
 * ── THE FIVE mm-* ARE THE MOCKUP MAKER'S DESIGNS ───────────────────────────
 * Imported from source--mockup-maker/src/templates. Their ids keep the maker's
 * own names (v2, v3, clean, studio, signature) behind an `mm-` prefix, because
 * those ids are what tie a design back to the repo it came from — v2 is
 * template--free-website-v2, and renaming it would throw that link away.
 *
 * They arrive as CSS rather than as the maker's React components on purpose.
 * That codebase renders each design from its own view model with its own theme
 * layer; porting five component trees would give this builder five ways to
 * lose content when somebody switches template, and would make the publish
 * route parse a page to undo a premium design instead of rewriting one
 * attribute. The LOOK is what was asked for, and the look is the CSS.
 */

/**
 * `worth` — what a designer would charge to build this page, in cents.
 * ============================================================================
 * MTC-FCS-052. It is the number the going-live toast slashes out, and it is a
 * COMPARISON, not a price of ours: nothing here has ever been sold for it and
 * nothing ever will be. It sits on the template because a heavier design is a
 * bigger job — which is what makes the figure move when somebody flips
 * through the designs.
 *
 * One number per design, declared here, so there is nowhere else for a price
 * to be typed and drift.
 */
const BUILT_IN_TEMPLATES = [
  {
    id: "classic",
    tier: "free",
    worth: 45000,
    label: "Classic",
    blurb: "Photograph across the top, services on cards.",
    premium: false,
  },
  {
    id: "editorial",
    tier: "premium",
    worth: 65000,
    label: "Editorial",
    blurb: "Light and centred, with plenty of white space.",
    premium: true,
  },

  /* ── From the Mockup Maker ───────────────────────────────────────────── */
  {
    id: "mm-v2",
    tier: "premium",
    worth: 80000,
    label: "Free Template 1",
    blurb: "Dark canvas, rounded cards, quote form in the hero.",
    premium: true,
    from: "mockup-maker/v2",
    /* Renders its OWN markup and stylesheet, ported from the maker, rather
       than re-dressing the shared one. Anything with this flag is a real
       design rather than a variation — see GeneratedSite.js. */
    ported: true,
  },
  {
    id: "mm-v3",
    tier: "premium",
    worth: 95000,
    label: "Free Template 2",
    blurb: "Light and photo-led, floating pill header.",
    premium: true,
    from: "mockup-maker/v3",
    ported: true,
  },
  {
    id: "mm-clean",
    tier: "premium",
    worth: 85000,
    label: "Free Template 3",
    blurb: "Light editorial, numbered service rows.",
    premium: true,
    from: "mockup-maker/clean",
    ported: true,
  },
  {
    id: "mm-studio",
    tier: "leads",
    worth: 90000,
    label: "Free Template 4",
    blurb: "Minimal editorial, square geometry, serif display.",
    premium: true,
    from: "mockup-maker/studio",
    ported: true,
  },
  {
    id: "mm-signature",
    tier: "leads",
    worth: 110000,
    label: "Free Template 5",
    blurb: "Logo-led, thin header bar, photo-first hero.",
    premium: true,
    from: "mockup-maker/signature",
    ported: true,
  },
]

const installedTemplateContext = require.context("../templates/library", true, /entry\.js$/)

/**
 * Installed designs that are NOT offered to anybody yet — FCS-124.
 * ============================================================================
 * A design is installed byte for byte (scripts/check-template-import.js proves it), but what it SAYS is whatever it was composed with:
 * a design made from a real company's website carries that company's address, hours, reviews and claims, and a page that keeps
 * the authored words publishes them under a stranger's name. A design with that problem is listed here, with the reason, and the
 * builder does not offer it (a draft that names it shows Classic). Withheld, not deleted: the package stays installed and
 * identical to the library, and putting it back is deleting its line. ONLY BEFORE A CUSTOMER HAS PUBLISHED WITH IT: a published site
 * names its design, and one whose design is not in this list is shown as "unavailable" (lib/site-content.js), not as Classic.
 *
 * Empty now. `template-3` ("Valley Roofing") WAS here: it was composed from a real flooring company's website. It is back because
 * its page is drawn from what the contractor told us (the shapes in src/templates/runtime/honest.js), its own copy has been taken
 * out of the document, and scripts/check-template-import.js reads every design for leftover words before it can be offered.
 */
export const WITHHELD_TEMPLATES = {}

export const INSTALLED_SITE_TEMPLATES = installedTemplateContext
  .keys()
  .map(key => installedTemplateContext(key).default)
  .filter(entry => !WITHHELD_TEMPLATES[entry.id])
  /* A design installed from the library states its own tier, or lands in
     Premium — FCS-077. Defaulted HERE rather than in each entry.js so a
     template dropped in from the library repo is grouped without having to
     know this product's tiers.

     Temporary: FCS-073 gives the admin board authority over which tier a
     design belongs to, and once the builder reads site_templates this
     default is only the value a design arrives with. */
  .map(entry => ({ tier: entry.premium === false ? "free" : "premium", ...entry }))

/**
 * `tier` — which group a design is traversed in — FCS-077.
 * ============================================================================
 * The picker used to walk every design in one list, so somebody on Premium
 * flicked through Lead Generation designs they could not have, and the count
 * described a catalogue rather than the choice in front of them. Each tier is
 * its own group now.
 *
 * The group shown follows the PLAN, not the design currently on screen — see
 * TemplatePicker for why keying it to the design trapped people on Classic.
 *
 * Separate from `premium`, which is the GATE: whether a plan may RENDER a
 * design at all. A design can be shown in one group and allowed by another
 * tier, and one field for both would make that unsayable.
 */
export const TEMPLATE_TIERS = [
  { id: "free", label: "Free" },
  { id: "premium", label: "Premium" },
  { id: "leads", label: "Lead Generation" },
]

export const SITE_TEMPLATES = [
  ...BUILT_IN_TEMPLATES,
  ...INSTALLED_SITE_TEMPLATES.map(({ Component, ...metadata }) => metadata),
]

export const DEFAULT_TEMPLATE = SITE_TEMPLATES[0]

/** True when the template brings its own markup instead of restyling ours. */
export function isPorted(id) {
  return Boolean(templateById(id).ported)
}

/** Anything unrecognised is Classic. A wrong id must not blank the page. */
export function templateById(id) {
  return SITE_TEMPLATES.find(template => template.id === id) || DEFAULT_TEMPLATE
}

/** The designs in one tier's group, in the order they are declared. */
export function templatesForTier(tier) {
  const group = SITE_TEMPLATES.filter(template => (template.tier || "premium") === tier)
  // A tier with no designs of its own falls back to the whole list rather
  // than showing a picker with nothing in it.
  return group.length > 0 ? group : SITE_TEMPLATES
}

/** The group a given design is traversed in. */
export function groupFor(templateId) {
  const template = templateById(templateId)
  return templatesForTier(template.tier || "premium")
}
